import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Chip, IconButton, Tooltip, Switch } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TimelineIcon from "@mui/icons-material/Timeline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { setModal } from "../../features/modal/ModalSlice";
import CustomButton from "../../components/Button/CustomButton";

const Workflows = () => {
  const dispatch = useDispatch();
  const modal = useSelector((state) => state.modal?.modal);

  const [workflows, setWorkflows] = useState([
    {
      id: 1,
      name: "Welcome Call Campaign",
      description: "Automated welcome calls for new customers",
      trigger: "New Customer",
      actions: 4,
      status: "Active",
      executions: 234,
      successRate: 92,
      lastRun: "2024-01-15 11:30 AM",
      createdAt: "2024-01-10",
    },
    {
      id: 2,
      name: "Appointment Reminder",
      description: "Send reminders 24 hours before appointments",
      trigger: "Scheduled",
      actions: 3,
      status: "Active",
      executions: 567,
      successRate: 88,
      lastRun: "2024-01-15 09:00 AM",
      createdAt: "2024-01-08",
    },
    {
      id: 3,
      name: "Payment Follow-up",
      description: "Follow up on pending payments",
      trigger: "Payment Due",
      actions: 5,
      status: "Active",
      executions: 145,
      successRate: 76,
      lastRun: "2024-01-15 08:00 AM",
      createdAt: "2024-01-05",
    },
    {
      id: 4,
      name: "Customer Feedback",
      description: "Collect feedback after service completion",
      trigger: "Service Complete",
      actions: 3,
      status: "Paused",
      executions: 89,
      successRate: 94,
      lastRun: "2024-01-12 03:00 PM",
      createdAt: "2024-01-03",
    },
    {
      id: 5,
      name: "Lead Qualification",
      description: "Qualify and route incoming leads",
      trigger: "Webhook",
      actions: 6,
      status: "Active",
      executions: 423,
      successRate: 81,
      lastRun: "2024-01-15 10:45 AM",
      createdAt: "2024-01-01",
    },
    {
      id: 6,
      name: "Order Confirmation",
      description: "Confirm orders via phone call",
      trigger: "Order Placed",
      actions: 4,
      status: "Active",
      executions: 678,
      successRate: 96,
      lastRun: "2024-01-15 11:15 AM",
      createdAt: "2023-12-28",
    },
  ]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (modal?.isUpdatedIn === "WORKFLOWS") {
      fetchWorkflows();
    }
  }, [modal]);

  const fetchWorkflows = async () => {
    setLoading(true);
    try {
      setTimeout(() => setLoading(false), 500);
    } catch (error) {
      console.error("Error fetching workflows:", error);
      setLoading(false);
    }
  };

  const handleCreateWorkflow = () => {
    dispatch(setModal({ type: "CREATE_WORKFLOW" }));
  };

  const handleEditWorkflow = (workflow) => {
    dispatch(setModal({ type: "EDIT_WORKFLOW", data: workflow }));
  };

  const handleDeleteWorkflow = (workflow) => {
    if (window.confirm(`Delete workflow "${workflow.name}"?`)) {
      setWorkflows(workflows.filter((w) => w.id !== workflow.id));
    }
  };

  const handleDuplicateWorkflow = (workflow) => {
    const newWorkflow = {
      ...workflow,
      id: workflows.length + 1,
      name: `${workflow.name} (Copy)`,
      executions: 0,
    };
    setWorkflows([...workflows, newWorkflow]);
  };

  const handleToggleStatus = (workflow) => {
    setWorkflows(
      workflows.map((w) =>
        w.id === workflow.id
          ? { ...w, status: w.status === "Active" ? "Paused" : "Active" }
          : w
      )
    );
  };

  const handleRunWorkflow = (workflow) => {
    alert(`Running workflow: ${workflow.name}`);
  };

  const handleViewAnalytics = (workflow) => {
    dispatch(setModal({ type: "VIEW_WORKFLOW_ANALYTICS", data: workflow }));
  };

  const getTriggerColor = (trigger) => {
    const colors = {
      Scheduled: { bg: "#E3F2FD", color: "#1565C0" },
      Webhook: { bg: "#F3E5F5", color: "#7B1FA2" },
      "New Customer": { bg: "#E8F5E9", color: "#2E7D32" },
      "Payment Due": { bg: "#FFF3E0", color: "#E65100" },
      "Service Complete": { bg: "#E0F2F1", color: "#00695C" },
      "Order Placed": { bg: "#FFF9C4", color: "#F57F17" },
    };
    return colors[trigger] || { bg: "#F5F5F5", color: "#666" };
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Workflow Name",
        size: 220,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <TimelineIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {row.original.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.original.description}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        accessorKey: "trigger",
        header: "Trigger",
        size: 140,
        Cell: ({ cell }) => {
          const colors = getTriggerColor(cell.getValue());
          return (
            <Chip
              label={cell.getValue()}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                color: colors.color,
                fontWeight: 600,
              }}
            />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 80,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600} color="primary">
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={row.original.status === "Active"}
              onChange={() => handleToggleStatus(row.original)}
              size="small"
            />
            <Typography variant="body2" fontWeight={500}>
              {row.original.status}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "executions",
        header: "Executions",
        size: 100,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600}>
            {cell.getValue().toLocaleString()}
          </Typography>
        ),
      },
      {
        accessorKey: "successRate",
        header: "Success Rate",
        size: 120,
        Cell: ({ cell }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 60,
                height: 6,
                backgroundColor: "#E0E0E0",
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${cell.getValue()}%`,
                  height: "100%",
                  backgroundColor:
                    cell.getValue() >= 90
                      ? "#4CAF50"
                      : cell.getValue() >= 70
                      ? "#FF9800"
                      : "#F44336",
                  borderRadius: 3,
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight={600}>
              {cell.getValue()}%
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "lastRun",
        header: "Last Run",
        size: 140,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
            {cell.getValue()}
          </Typography>
        ),
      },
    ],
    []
  );

  const totalWorkflows = workflows.length;
  const activeWorkflows = workflows.filter((w) => w.status === "Active").length;
  const totalExecutions = workflows.reduce((sum, w) => sum + w.executions, 0);
  const avgSuccessRate = (
    workflows.reduce((sum, w) => sum + w.successRate, 0) / workflows.length
  ).toFixed(0);

  return (
    <Box>
      {/* Header Section - Keep as is */}
      <Box sx={{
        backgroundColor: "#FFFFFF",
        borderBottom: "1px solid #E5E7EB",
        px: 4,
        py: 3
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h5" fontWeight={600} color="#111827" sx={{ mb: 0.5 }}>
              Workflows
            </Typography>
            <Typography variant="body2" color="#6B7280">
              Automate customer communication with visual workflow builder
            </Typography>
          </Box>
          <CustomButton
            icon={<AddIcon />}
            iconPosition="left"
            onClick={handleCreateWorkflow}
            text="Create Workflow"
          />
        </Box>
      </Box>

      {/* Stats Cards Section - Separate */}
      <Box sx={{ px: 4, pt: 3, pb: 2 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#111827" sx={{ mb: 0.5 }}>
              {totalWorkflows}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Total Workflows
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#10B981" sx={{ mb: 0.5 }}>
              {activeWorkflows}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Active Workflows
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#3B82F6" sx={{ mb: 0.5 }}>
              {totalExecutions.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Total Executions
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#F59E0B" sx={{ mb: 0.5 }}>
              {avgSuccessRate}%
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Average Success Rate
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Table Section */}
      <Box sx={{ px: 4, pb: 3 }}>
        <Box sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: 2,
          border: "1px solid #E5E7EB",
          overflow: "hidden",
          p: 2
        }}>
          <MaterialReactTable
            columns={columns}
            data={workflows}
            state={{ isLoading: loading }}
            enableRowActions
            positionActionsColumn="last"
            renderRowActions={({ row }) => (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Tooltip title="Run Now">
                  <IconButton
                    size="small"
                    onClick={() => handleRunWorkflow(row.original)}
                    sx={{ color: "success.main" }}
                  >
                    <PlayArrowIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="View Analytics">
                  <IconButton
                    size="small"
                    onClick={() => handleViewAnalytics(row.original)}
                    sx={{ color: "info.main" }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => handleEditWorkflow(row.original)}
                    sx={{ color: "primary.main" }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Duplicate">
                  <IconButton
                    size="small"
                    onClick={() => handleDuplicateWorkflow(row.original)}
                    sx={{ color: "warning.main" }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteWorkflow(row.original)}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            enableColumnFilters
            enableGlobalFilter
            enableSorting
            enablePagination
            initialState={{
              pagination: { pageSize: 10, pageIndex: 0 },
            }}
            muiTablePaperProps={{
              elevation: 0,
              sx: {
                borderRadius: 0,
                border: "none",
                boxShadow: "none"
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: "#F9FAFB",
                fontWeight: 600,
              },
            }}
            muiTableBodyProps={{
              sx: {
                '& tr:hover': {
                  backgroundColor: '#F9FAFB',
                },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Workflows;
